import { useCallback, useRef, useState } from 'react';

import type { StepId } from '@modules/_shared/components/create-transaction/hooks/useTransactionStepper';
import { useAnalytics } from '@shared/analytics';
import { ANALYTICS_EVENTS } from '@shared/analytics/constants';

type UseTransactionStepNavigationOptions = {
  currentStep: StepId;
  flowId: string;
  isLastStep: boolean;
  nextLabel: string | undefined;
  submit: () => void;
  validateAndGoNext: () => Promise<boolean>;
};

export const useTransactionStepNavigation = ({
  currentStep,
  flowId,
  isLastStep,
  nextLabel,
  submit,
  validateAndGoNext,
}: UseTransactionStepNavigationOptions) => {
  const { track } = useAnalytics();
  const [addressStepLoading, setAddressStepLoading] = useState(false);
  const addressStepSearchStartTimeRef = useRef<number | null>(null);

  const handleAddressLoadingStateChange = useCallback(
    (isLoading: boolean, searchStartTime: number | null) => {
      setAddressStepLoading(isLoading);
      addressStepSearchStartTimeRef.current = searchStartTime;
    },
    [],
  );

  const handleNext = useCallback(async () => {
    if (isLastStep) {
      submit();
      return;
    }

    if (currentStep === 'address' && addressStepLoading) {
      const duration_ms = addressStepSearchStartTimeRef.current
        ? Date.now() - addressStepSearchStartTimeRef.current
        : null;
      track(ANALYTICS_EVENTS.TRANSACTION_CREATION_LISTINGS_SEARCH_SKIPPED, {
        flow_id: flowId,
        duration_ms,
      });
    }

    const isValid = await validateAndGoNext();

    if (isValid && currentStep === 'address' && nextLabel) {
      track(ANALYTICS_EVENTS.TRANSACTION_CREATION_DIFFERENT_PROPERTY_SOLD, {
        flow_id: flowId,
      });
    }
  }, [
    addressStepLoading,
    currentStep,
    flowId,
    isLastStep,
    nextLabel,
    submit,
    track,
    validateAndGoNext,
  ]);

  return { handleAddressLoadingStateChange, handleNext };
};
